import * as React from 'react';
import { Component } from 'react';
import propz from 'propz';
import { QUESTIONS_TEMPLATE_TYPE } from 'Src/consts/message';
import { Map } from 'Src/components/Map/Map';
import { Button } from 'Src/components/Button/Button';
import { Select } from 'Src/components/Select/Select';
import {
  getSelectOptionForBooleanQuestion,
  getSelectOptionForClubInvitationEnumQuestion
} from 'Src/helpers/table/select';
import { isNumeric } from 'Src/helpers/common/common';
import { formatAgesCompactForSchool } from 'Src/helpers/ageGroups';
import { TournamentConfirmationRequestMessage } from 'Src/models/message';
import { TournamentInfo } from './TournamentInfo/TournamentInfo';
import { MessageField } from 'Src/models/message';
import { FormDefinition, FormSubmission } from 'Src/types/formSchema';
import { FormRendererV2 } from 'Src/components/FormRendererV2/FormRendererV2';
import { applyAnswersToLegacyFields, formDefinitionToLegacyFields } from 'Src/helpers/form/legacyFormAdapter';
import {
  FormAnswerMap,
  FormErrorMap,
  answerMapToArray,
  answersToMap,
  validateFormDefinition
} from 'Src/helpers/form/formRuntime';
import { getSubmissionElementIds, hasFormFlow } from 'Src/helpers/form/formFlowRuntime';
import { AppUser } from 'Src/views/App/App';

interface Props {
  message: TournamentConfirmationRequestMessage;
  user: AppUser;
  isEditable: boolean;
  onAcceptClick: (data: MessageField[], submission?: FormSubmission) => void;
  onCloseClick: () => void;
  onDirtyChange?: (isDirty: boolean) => void;
}

interface State {
  fields: MessageField[];
  isMapVisible: boolean;
  activeSelectIndex: number[];
  errors: Error[];
  definition?: FormDefinition;
  answers: FormAnswerMap;
  formErrors: FormErrorMap;
  isFormFlowComplete: boolean;
}

interface Error {
  isError: boolean;
  errorText: string;
}

export class ConfirmationRequestForm extends Component<Props, State> {
  constructor(props) {
    super(props);
    const { message } = this.props;
    const fields = propz.get(message, ['fields']);

    let fieldsUpdated;
    let activeSelectIndex = [];
    let errors = [];

    const isFieldsExist = typeof fields !== 'undefined' && fields.length > 0;
    if (isFieldsExist) {
      fieldsUpdated = fields.map((field, index) => {
        activeSelectIndex[index] = 0;
        errors[index] = { isError: false, errorText: '' };
        return { ...field, value: '' };
      });
    }

    this.state = {
      fields: isFieldsExist ? fieldsUpdated : [],
      isMapVisible: false,
      activeSelectIndex: isFieldsExist ? activeSelectIndex : [],
      errors: isFieldsExist ? errors : [],
      definition: message.formSchemaVersion === 2 && message.formDefinition ? message.formDefinition : undefined,
      answers: answersToMap(message.formAnswers || []),
      formErrors: {},
      isFormFlowComplete: false
    };
  }

  onSubmit = () => {
    const { fields, errors, definition, answers } = this.state;
    if (definition) {
      const formErrors = validateFormDefinition(definition, answers, getSubmissionElementIds(definition, answers));
      if (Object.keys(formErrors).length > 0) {
        this.setState({ formErrors });
        return;
      }
      const answerList = answerMapToArray(answers);
      const fieldsUpdated = applyAnswersToLegacyFields(
        formDefinitionToLegacyFields(definition),
        definition,
        answerList
      ) as MessageField[];
      this.props.onAcceptClick(fieldsUpdated, {
        formSchemaVersion: 2,
        formAnswers: answerList
      });
      return;
    }
    let submitAbility = true;
    let fieldsUpdated = [...fields];

    const errorsUpdated = errors.map(item => {
      return {
        isError: false,
        errorText: ''
      };
    });

    fields.forEach((field, index) => {
      const isNumericField = isNumeric(field.value);
      const isNumberType = field.type === QUESTIONS_TEMPLATE_TYPE.NUMBER;
      const isBooleanType = field.type === QUESTIONS_TEMPLATE_TYPE.BOOLEAN;
      const isEnumType = field.type === QUESTIONS_TEMPLATE_TYPE.ENUM;
      const isFieldEmpty = field.value === '';

      if (isBooleanType && isFieldEmpty) {
        const options = getSelectOptionForBooleanQuestion();
        const value = options[0].value;
        propz.set(fieldsUpdated[index], ['value'], value);
      }

      if (isEnumType && isFieldEmpty) {
        const options = getSelectOptionForClubInvitationEnumQuestion(field.enumOptions);
        const value = options[0].value;
        propz.set(fieldsUpdated[index], ['value'], value);
      }

      if (isNumberType && !isNumericField && !isFieldEmpty) {
        errorsUpdated[index].isError = true;
        errorsUpdated[index].errorText = 'please enter a number';
        submitAbility = false;
      }

      const isFieldUpdatedEmpty = fieldsUpdated[index].value === '';

      if (isFieldUpdatedEmpty && field.isRequired) {
        errorsUpdated[index].isError = true;
        errorsUpdated[index].errorText = 'required';
        submitAbility = false;
      }
    });

    if (submitAbility) {
      this.props.onAcceptClick(fieldsUpdated);
    } else {
      this.setState({
        errors: errorsUpdated
      });
    }
  };

  toggleMap = () => {
    const { isMapVisible } = this.state;
    this.setState({
      isMapVisible: !isMapVisible
    });
  };

  render() {
    const { message, isEditable, onAcceptClick, onCloseClick } = this.props;
    const {
      activeSelectIndex,
      errors,
      fields,
      isMapVisible,
      definition,
      answers,
      formErrors,
      isFormFlowComplete
    } = this.state;
    const picUrl = propz.get(message, ['schoolData', 'pic'], '');
    const isSchoolLogoExist = picUrl !== '';
    const fieldsUpdated = [...fields];
    const isFieldsExist = fields.length > 0;
    const point = propz.get(message, ['tournamentData', 'venue', 'point']);

    const ageChipText = formatAgesCompactForSchool(
      propz.get(message, ['tournamentData', 'ages'], []),
      propz.get(message, ['schoolData'], null)
    );

    const formatTournamentWindow = (startRaw: any, endRaw: any) => {
      if (!startRaw) return '';

      const start = new Date(startRaw);
      if (Number.isNaN(start.getTime())) return '';

      const pad2 = (n: number) => (String(n).length >= 2 ? String(n) : `0${n}`);

      const formatDate = (d: Date) => `${pad2(d.getDate())}/${pad2(d.getMonth() + 1)}/${d.getFullYear()}`;

      const formatTime = (d: Date) => `${pad2(d.getHours())}:${pad2(d.getMinutes())}`;

      if (!endRaw) {
        return `${formatDate(start)} ${formatTime(start)}`;
      }

      const end = new Date(endRaw);
      if (Number.isNaN(end.getTime())) {
        return `${formatDate(start)} ${formatTime(start)}`;
      }

      const isSameDay =
        start.getFullYear() === end.getFullYear() &&
        start.getMonth() === end.getMonth() &&
        start.getDate() === end.getDate();

      if (isSameDay) {
        return `${formatDate(start)} ${formatTime(start)} - ${formatTime(end)}`;
      }

      return `${formatDate(start)} ${formatTime(start)} - ${formatDate(end)} ${formatTime(end)}`;
    };

    const tournamentWindowText = formatTournamentWindow(
      propz.get(message, ['tournamentData', 'startTime'], null),
      propz.get(message, ['tournamentData', 'endTime'], null)
    );

    const deadlineRaw = propz.get(message, ['deadlineForAnswers'], null);
    const deadlineDate = deadlineRaw ? new Date(deadlineRaw) : null;
    const now = new Date();

    const formatDeadlineCountdown = (ms: number) => {
      const totalMinutes = Math.max(0, Math.floor(ms / 60000));
      const h = Math.floor(totalMinutes / 60);
      const m = totalMinutes % 60;

      if (h <= 0) return `${m}m left`;
      return `${h}h ${m}m left`;
    };

    let deadlineBadge: React.ReactNode = null;

    if (deadlineDate && !Number.isNaN(deadlineDate.getTime())) {
      const msLeft = deadlineDate.getTime() - now.getTime();

      let badgeClass = 'badge badge-light border';
      let badgeText = `Reply by ${deadlineDate.toLocaleString()}`;

      if (msLeft < 0) {
        badgeClass = 'badge badge-secondary';
        badgeText = `Deadline passed • ${deadlineDate.toLocaleString()}`;
      } else if (msLeft <= 2 * 3600 * 1000) {
        badgeClass = 'badge badge-danger';
        badgeText = `Reply by ${deadlineDate.toLocaleString()} • ${formatDeadlineCountdown(msLeft)}`;
      } else if (msLeft <= 24 * 3600 * 1000) {
        badgeClass = 'badge badge-danger';
        badgeText = `Reply by ${deadlineDate.toLocaleString()}`;
      } else if (msLeft <= 72 * 3600 * 1000) {
        badgeClass = 'badge badge-warning';
        badgeText = `Reply by ${deadlineDate.toLocaleString()}`;
      } else {
        badgeClass = 'badge badge-success';
        badgeText = `Reply by ${deadlineDate.toLocaleString()}`;
      }

      deadlineBadge = (
        <span className={`${badgeClass} mr-2 mb-2`} title="Deadline for your response">
          {badgeText}
        </span>
      );
    }

    return (
      <>
        <div className="programmeBookingModal mb-4">
          <div className="programmeBookingModalHero mb-4">
            <div className="programmeBookingModalHeroTop">
              <div>
                <div className="programmeBookingModalEyebrow">Tournament confirmation request</div>
                <div className="programmeBookingModalClubName">
                  {String(propz.get(message, ['tournamentData', 'name'], 'Tournament'))}
                </div>
              </div>

              <div className="programmeBookingModalBadges">
                {deadlineBadge}

                {propz.get(message, ['schoolData', 'name']) ? (
                  <span className="badge badge-light border mr-2 mb-2">
                    {String(propz.get(message, ['schoolData', 'name'], ''))}
                  </span>
                ) : null}

                {ageChipText ? <span className="badge badge-light border mr-2 mb-2">Ages: {ageChipText}</span> : null}
              </div>
            </div>

            <div className="programmeBookingModalMeta mt-2">
              {tournamentWindowText ? <span>Tournament window: {tournamentWindowText}</span> : null}

              {propz.get(message, ['tournamentData', 'venue', 'postcode']) ? (
                <span>Venue: {String(propz.get(message, ['tournamentData', 'venue', 'postcode'], ''))}</span>
              ) : null}
            </div>

            <div className="programmeBookingModalInviteNote mt-3">
              Please confirm whether your school will participate, and answer the questions below so the organiser can
              plan the event properly.
            </div>
          </div>

          {isMapVisible && <Map point={point} customStylingClass="mb-3 eClubWizardMap" isMarkerDraggable={false} />}

          {point ? (
            <div className="mb-3">
              <button type="button" className="btn btn-outline-secondary btn-sm" onClick={this.toggleMap}>
                {isMapVisible ? 'Hide map' : 'View map'}
              </button>
            </div>
          ) : null}
        </div>

        <div className="programmeBookingModal">
          {definition ? (
            <FormRendererV2
              definition={definition}
              answers={answers}
              errors={formErrors}
              disabled={!isEditable}
              onChange={nextAnswers =>
                this.setState(
                  {
                    answers: nextAnswers,
                    formErrors: {},
                    isFormFlowComplete: hasFormFlow(definition) ? false : isFormFlowComplete
                  },
                  () => this.props.onDirtyChange && this.props.onDirtyChange(true)
                )
              }
              onSubmitRequested={() => this.setState({ isFormFlowComplete: true })}
            />
          ) : (
            isFieldsExist &&
            fieldsUpdated.map((field, index) => {
              const isRequired = !!field.isRequired;
              const isBoolean = field.type === QUESTIONS_TEMPLATE_TYPE.BOOLEAN;
              const isEnum = field.type === QUESTIONS_TEMPLATE_TYPE.ENUM;
              const isNumber = field.type === QUESTIONS_TEMPLATE_TYPE.NUMBER;
              const isString = field.type === QUESTIONS_TEMPLATE_TYPE.STRING;

              return (
                <div key={`club_invitation_form_field_${index}`} className="programmeBookingQuestionCard mb-3">
                  <div className="d-flex justify-content-between align-items-start mb-2 flex-wrap" style={{ gap: 8 }}>
                    <label className="font-weight-bold mb-0">
                      {field.heading}
                      {isRequired ? <span className="text-danger ml-1">*</span> : null}
                    </label>

                    <div className="d-flex flex-wrap" style={{ gap: 6 }}>
                      <span className="badge badge-light border">
                        {isString
                          ? 'Text'
                          : isNumber
                          ? 'Number'
                          : isBoolean
                          ? 'Yes / No'
                          : isEnum
                          ? 'Options'
                          : 'Question'}
                      </span>
                      <span className={`badge ${isRequired ? 'badge-warning' : 'badge-secondary'}`}>
                        {isRequired ? 'Required' : 'Optional'}
                      </span>
                    </div>
                  </div>

                  {(isString || isNumber) && (
                    <input
                      type={isNumber ? 'number' : 'text'}
                      className="form-control"
                      value={field.value}
                      onChange={event => {
                        propz.set(fieldsUpdated[index], ['value'], event.target.value);
                        this.setState(
                          { fields: fieldsUpdated },
                          () => this.props.onDirtyChange && this.props.onDirtyChange(true)
                        );
                      }}
                    />
                  )}

                  {isBoolean && (
                    <Select
                      onChange={value => {
                        const options = getSelectOptionForBooleanQuestion();
                        const nextIndex = options.findIndex(option => option.value === value);
                        const activeSelectIndexUpdated = [...activeSelectIndex];
                        activeSelectIndexUpdated[index] = nextIndex;
                        propz.set(fieldsUpdated[index], ['value'], value);

                        this.setState(
                          {
                            activeSelectIndex: activeSelectIndexUpdated,
                            fields: fieldsUpdated
                          },
                          () => this.props.onDirtyChange && this.props.onDirtyChange(true)
                        );
                      }}
                      options={getSelectOptionForBooleanQuestion()}
                      activeResultIndex={activeSelectIndex[index]}
                      customClass={'form-control'}
                    />
                  )}

                  {isEnum && (
                    <Select
                      onChange={value => {
                        const options = getSelectOptionForClubInvitationEnumQuestion(field.enumOptions);
                        const nextIndex = options.findIndex(option => option.value === value);
                        const activeSelectIndexUpdated = [...activeSelectIndex];
                        activeSelectIndexUpdated[index] = nextIndex;
                        propz.set(fieldsUpdated[index], ['value'], value);

                        this.setState(
                          {
                            activeSelectIndex: activeSelectIndexUpdated,
                            fields: fieldsUpdated
                          },
                          () => this.props.onDirtyChange && this.props.onDirtyChange(true)
                        );
                      }}
                      options={getSelectOptionForClubInvitationEnumQuestion(field.enumOptions)}
                      activeResultIndex={activeSelectIndex[index]}
                      customClass={'form-control'}
                    />
                  )}

                  {errors[index].isError ? (
                    <div className="alert alert-danger mt-3 mb-0">{errors[index].errorText}</div>
                  ) : (
                    <div className="small text-muted mt-2">
                      {isRequired
                        ? 'This answer is required before you can submit.'
                        : 'You can leave this blank if needed.'}
                    </div>
                  )}
                </div>
              );
            })
          )}
        </div>
        {isEditable && (
          <div className="d-flex justify-content-end mt-4" style={{ gap: 10 }}>
            <button type="button" className="btn btn-outline-secondary" onClick={onCloseClick}>
              Close
            </button>

            <button
              type="button"
              className="btn btn-primary"
              onClick={this.onSubmit}
              disabled={!!definition && hasFormFlow(definition) && !isFormFlowComplete}
            >
              Accept
            </button>
          </div>
        )}
      </>
    );
  }
}
